/**
 * Live voice with the agent builder — ported from Yaven's Eve (escape-room web client + the mobile
 * session hook).
 *
 * The browser talks to the realtime provider directly over WebRTC (full-duplex audio, barge-in) with a
 * short-lived secret our server minted; the server decided the instructions and the tools. Everything
 * else goes through our API: the model's tool calls are forwarded to /agent-builder/voice/sessions/:id/tool
 * (the server runs them for this adviser only), a heartbeat every 20 s lets the server bill the elapsed
 * slice and tell us when to stop, and the end sends the transcript, which joins the builder chat.
 *
 * NOTHING OUTLIVES THE PAGE: a start that returns from a wait and finds the user gone ends the session it
 * created; leaving the page ends the conversation.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const HEARTBEAT_MS = 20000;
const END_TEXT = {
  balance_exhausted: 'You are out of AI tokens for voice.',
  session_cap: 'This conversation reached its time limit.',
  daily_cap: "Today's voice limit is reached.",
  timeout: 'The connection was lost.',
};

export default function useBuilderVoice(api, { language, onToolResult, onEnded }) {
  const [phase, setPhase] = useState('off');       // off | connecting | listening | thinking | speaking
  const [caption, setCaption] = useState('');
  const [error, setError] = useState('');
  const [muted, setMuted] = useState(false);
  const [working, setWorking] = useState('');
  const [minutesLeft, setMinutesLeft] = useState(null);

  const pc = useRef(null), channel = useRef(null), stream = useRef(null), audio = useRef(null);
  const sessionId = useRef(null), heartbeat = useRef(null), live = useRef(false), run = useRef(0), mounted = useRef(true);
  const transcript = useRef([]), replyText = useRef('');
  const cb = useRef({ onToolResult, onEnded });
  cb.current = { onToolResult, onEnded };

  const teardown = () => {
    if (heartbeat.current) { clearInterval(heartbeat.current); heartbeat.current = null; }
    try { channel.current?.close(); } catch { /* closed */ }
    try { pc.current?.close(); } catch { /* closed */ }
    channel.current = null; pc.current = null;
    stream.current?.getTracks().forEach(t => t.stop()); stream.current = null;
    if (audio.current) { audio.current.pause(); audio.current.srcObject = null; audio.current = null; }
  };

  const finish = useCallback(async (reason = 'user', message = '') => {
    run.current += 1;
    const id = sessionId.current;
    sessionId.current = null;
    live.current = false;
    teardown();
    if (mounted.current) { setPhase('off'); setCaption(''); setWorking(''); setMuted(false); if (message) setError(message); }
    if (id) {
      const lines = transcript.current.filter(l => l.text && l.text.trim()).slice(-200);
      transcript.current = [];
      try { await api.post(`/agent-builder/voice/sessions/${id}/end`, { reason, transcript: lines }); } catch { /* the sweeper ends it */ }
      cb.current.onEnded?.();
    }
  }, [api]);

  const runTool = async (e) => {
    const id = sessionId.current;
    let output = JSON.stringify({ error: 'Session ended' });
    if (id) {
      setWorking(e.name);
      try {
        const r = await api.post(`/agent-builder/voice/sessions/${id}/tool`, { callId: e.call_id, name: e.name, arguments: e.arguments || '{}' });
        output = r.data?.output || '{}';
        cb.current.onToolResult?.(r.data);
      } catch (err) {
        output = JSON.stringify({ error: err?.response?.data?.error || 'The tool failed' });
      } finally { if (mounted.current) setWorking(''); }
    }
    const dc = channel.current;
    if (dc && dc.readyState === 'open') {
      dc.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: e.call_id, output } }));
      dc.send(JSON.stringify({ type: 'response.create' }));
    }
  };

  const onEvent = (raw) => {
    let e;
    try { e = JSON.parse(raw); } catch { return; }   // never execute model output
    switch (e.type) {
      case 'input_audio_buffer.speech_started': setPhase('listening'); setCaption(''); break;
      case 'input_audio_buffer.speech_stopped': setPhase('thinking'); break;
      case 'conversation.item.input_audio_transcription.completed':
        if (e.transcript) transcript.current.push({ role: 'user', text: e.transcript });
        break;
      case 'response.output_audio_transcript.delta':
      case 'response.audio_transcript.delta':
        replyText.current += e.delta || ''; setCaption(replyText.current); setPhase('speaking'); break;
      case 'response.output_audio_transcript.done':
      case 'response.audio_transcript.done': {
        const text = e.transcript || replyText.current;
        if (text) transcript.current.push({ role: 'assistant', text });
        replyText.current = ''; break;
      }
      case 'response.done':
      case 'output_audio_buffer.stopped': setPhase('listening'); break;
      case 'response.function_call_arguments.done': runTool(e); break;
      case 'error': if (e.error?.message) setError(e.error.message); break;
      default: break;
    }
  };

  const start = useCallback(async ({ voice } = {}) => {
    if (live.current) return;
    const my = ++run.current;
    const left = () => run.current !== my || !mounted.current;
    live.current = true;
    transcript.current = []; replyText.current = '';
    setError(''); setCaption(''); setPhase('connecting');
    let started;
    try {
      const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false });
      if (left()) { mic.getTracks().forEach(t => t.stop()); return; }
      stream.current = mic;
      const r = await api.post('/agent-builder/voice/sessions', { voice, language });
      started = r.data;
      if (left()) { api.post(`/agent-builder/voice/sessions/${started.session.id}/end`, { reason: 'user', transcript: [] }).catch(() => {}); teardown(); return; }
      sessionId.current = started.session.id;
      setMinutesLeft(started.estimatedMinutesLeft ?? null);

      const peer = new RTCPeerConnection(); pc.current = peer;
      const out = new Audio(); out.autoplay = true; audio.current = out;
      peer.ontrack = ev => { out.srcObject = ev.streams[0]; out.play().catch(() => setError('Audio playback was blocked by the browser. End and press Talk again.')); };
      peer.onconnectionstatechange = () => { if (['failed', 'disconnected'].includes(peer.connectionState) && live.current) finish('error', 'The voice connection dropped.'); };
      mic.getTracks().forEach(t => peer.addTrack(t, mic));
      const dc = peer.createDataChannel('oai-events'); channel.current = dc;
      dc.onopen = () => {
        if (left()) return;
        setPhase('listening');
        // The builder speaks first: greets and picks up where the conversation is.
        if (started.openWith) dc.send(JSON.stringify({ type: 'response.create' }));
        heartbeat.current = setInterval(async () => {
          const id = sessionId.current;
          if (!id) return;
          try {
            const h = await api.post(`/agent-builder/voice/sessions/${id}/heartbeat`);
            setMinutesLeft(h.data?.estimatedMinutesLeft ?? null);
            if (h.data?.mustEnd) finish(h.data.endReason || 'user', END_TEXT[h.data.endReason] || 'The voice conversation ended.');
          } catch (err) {
            if ([404, 409].includes(err?.response?.status)) finish('user', 'The voice conversation ended.');
          }
        }, HEARTBEAT_MS);
      };
      dc.onmessage = ev => { if (live.current && mounted.current) onEvent(ev.data); };

      const offer = await peer.createOffer();
      await peer.setLocalDescription(offer);
      const sdp = await fetch(started.connectUrl, { method: 'POST', headers: { Authorization: `Bearer ${started.clientSecret}`, 'Content-Type': 'application/sdp' }, body: offer.sdp });
      if (!sdp.ok) throw new Error(`Voice connection failed (${sdp.status}).`);
      if (left()) return;
      await peer.setRemoteDescription({ type: 'answer', sdp: await sdp.text() });
    } catch (err) {
      const msg = err?.name === 'NotAllowedError' ? 'Allow microphone access to talk. You can keep typing.'
        : err?.response?.status === 402 ? 'Not enough AI tokens for a voice conversation. Top up in Settings › Tokens.'
          : err?.response?.data?.error || err?.message || 'Voice could not start.';
      await finish(sessionId.current ? 'error' : 'user', msg);
    }
  }, [api, language, finish]);

  /**
   * Tell the live conversation that something finished meanwhile (cards ready, a test done), so the voice
   * never says "still preparing" about work that is already on the screen. Spoken right away when the
   * builder is only listening; otherwise it is there for its next answer.
   */
  const notify = useCallback((text) => {
    const dc = channel.current;
    if (!dc || dc.readyState !== 'open' || !text) return;
    dc.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'message', role: 'system', content: [{ type: 'input_text', text: `[update] ${String(text).slice(0, 1500)}` }] } }));
    if (phase === 'listening') dc.send(JSON.stringify({ type: 'response.create', response: { instructions: 'Briefly tell the adviser, in their language, what just finished (from the [update]) and what they can do now. One or two sentences.' } }));
  }, [phase]);

  const toggleMute = useCallback(() => {
    setMuted(m => { const next = !m; stream.current?.getAudioTracks().forEach(t => { t.enabled = !next; }); return next; });
  }, []);

  useEffect(() => {
    mounted.current = true;
    // A closed tab cannot send an authenticated request; its session stops heartbeating and the server's
    // sweeper ends it, billed only up to the last heartbeat.
    return () => {
      mounted.current = false;
      // One step later: React's development double-mount must not end a conversation.
      setTimeout(() => { if (!mounted.current && (live.current || sessionId.current)) finish('user'); }, 0);
    };
  }, [finish]);

  return { phase, caption, error, setError, muted, working, minutesLeft, start, end: () => finish('user'), toggleMute, notify, active: phase !== 'off' };
}
